import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link2, Copy, Ban, Loader2, Camera, Plus, Lock } from 'lucide-react';
import { API } from '../../lib/constants';
import { formatApiErrorDetail } from '../../context/AuthContext';
import { Field } from '../AuthShell';
import { copyText } from '../../lib/clipboard';

const STATUS_BADGE = { active: 'badge-lime', expired: 'badge-muted', revoked: 'badge-danger' };

export const PhotographersPanel = () => {
  const [links, setLinks] = useState([]);
  const [name, setName] = useState('');
  const [hours, setHours] = useState(24);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => axios.get(`${API}/admin/upload-links`).then((r) => setLinks(r.data)).catch(() => toast.error('Failed to load upload links')), []);
  useEffect(() => { load(); }, [load]);

  const urlFor = (token) => `${window.location.origin}/u/${token}`;

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/admin/upload-links`, { photographer_name: name, expires_in_hours: Number(hours) });
      const copied = await copyText(urlFor(data.token));
      toast.success(`Upload link for ${data.photographer_name} created${copied ? ' & copied' : ''}`);
      setName('');
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Could not create link');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (token) => {
    try {
      await axios.delete(`${API}/admin/upload-links/${token}`);
      toast.success('Link revoked');
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Could not revoke link');
    }
  };

  const copy = async (token) => { (await copyText(urlFor(token))) ? toast.success('Upload link copied') : toast.error('Copy blocked — select the link manually'); };

  return (
    <div className="space-y-6" data-testid="photographers-panel">
      <form onSubmit={create} className="card-brutal p-6 grid grid-cols-1 md:grid-cols-[1fr_160px_auto] gap-4 items-end">
        <Field label="Photographer name">
          <input data-testid="link-name-input" className="input" placeholder="e.g. Rahul · Camera 2" value={name} onChange={(e) => setName(e.target.value)} required maxLength={60} />
        </Field>
        <Field label="Expires in (hours)">
          <input data-testid="link-hours-input" type="number" min={1} max={168} className="input" value={hours} onChange={(e) => setHours(e.target.value)} />
        </Field>
        <button data-testid="create-link-btn" type="submit" disabled={creating || !name.trim()} className="btn-primary !py-3">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create link
        </button>
        <p className="md:col-span-3 font-mono text-[11px] text-slate-500">Photographers open the link on any device and upload straight into this event's folder — no login. Links can be used repeatedly until they expire or you revoke them.</p>
      </form>

      <div className="card" data-testid="upload-links-card">
        <div className="px-6 py-4 border-b-2 border-line flex items-center gap-2">
          <Camera className="w-4 h-4 text-cyan" />
          <h3 className="h-display text-sm">Upload links</h3>
          <span className="font-mono text-[11px] text-slate-500 ml-auto">{links.filter((l) => l.status === 'active').length} active</span>
        </div>
        {links.length === 0 ? (
          <p className="px-6 py-10 font-mono text-xs uppercase tracking-[0.2em] text-slate-500 text-center" data-testid="no-upload-links">No upload links yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-dark" data-testid="upload-links-table">
              <thead><tr>{['Photographer', 'Status', 'Uploads', 'Expires', ''].map((h) => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {links.map((l, i) => (
                  <tr key={l.token} data-testid={`upload-link-row-${i}`}>
                    <td className="font-semibold text-white">{l.photographer_name}<div className="font-mono text-[10px] text-slate-500 mt-0.5 truncate max-w-[260px]">/u/{l.token}</div></td>
                    <td><span className={STATUS_BADGE[l.status]} data-testid={`upload-link-status-${i}`}>{l.status}</span></td>
                    <td>{l.photo_count} photos · {l.upload_count} batch{l.upload_count === 1 ? '' : 'es'}</td>
                    <td className="text-slate-400">{new Date(l.expires_at).toLocaleString()}</td>
                    <td>
                      <div className="flex gap-1.5 justify-end">
                        <button data-testid={`copy-link-${i}-btn`} onClick={() => copy(l.token)} className="icon-btn" title="Copy link"><Copy className="w-4 h-4" /></button>
                        <a href={urlFor(l.token)} target="_blank" rel="noreferrer" className="icon-btn" title="Open" data-testid={`open-link-${i}-btn`}><Link2 className="w-4 h-4" /></a>
                        {l.status === 'active' && (l.is_demo ? (
                          <span className="icon-btn opacity-60 cursor-not-allowed" title="Protected demo link" data-testid={`protected-link-${i}`}><Lock className="w-4 h-4" /></span>
                        ) : (
                          <button data-testid={`revoke-link-${i}-btn`} onClick={() => revoke(l.token)} className="icon-btn hover:!border-red-500 hover:!text-red-400" title="Revoke"><Ban className="w-4 h-4" /></button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
